import { useState, useEffect } from "react";
import api from "../utils/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Building2, Users, MapPin, TrendingUp, Clock, CheckCircle, Database, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BACKEND_URL, API_URL } from '../config/api';

// BACKEND_URL imported from config/api
const PLATFORM_API = `${BACKEND_URL}/api/platform`;

const DashboardStats = () => {
  const [stats, setStats] = useState({
    totalOrgs: 0,
    activeOrgs: 0,
    pendingOrgs: 0,
    suspendedOrgs: 0,
    trialOrgs: 0,
    totalLocations: 0,
    recentSignups: [],
    orgsByPlan: { free: 0, pro: 0, enterprise: 0 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      
      // Fetch organizations
      const orgsResponse = await axios.get(`${PLATFORM_API}/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const orgs = orgsResponse.data;
      
      // Fetch locations
      const locsResponse = await axios.get(`${PLATFORM_API}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const locs = locsResponse.data;
      
      // Calculate stats
      const activeOrgs = orgs.filter(o => o.status === 'active').length;
      const pendingOrgs = orgs.filter(o => o.status === 'pending').length;
      const suspendedOrgs = orgs.filter(o => o.status === 'suspended').length;
      const trialOrgs = orgs.filter(o => o.status === 'trial').length;
      
      const orgsByPlan = {
        free: orgs.filter(o => o.plan === 'free').length,
        pro: orgs.filter(o => o.plan === 'pro').length,
        enterprise: orgs.filter(o => o.plan === 'enterprise').length
      };
      
      // Get recent signups (last 5)
      const recentSignups = orgs
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5);
      
      setStats({
        totalOrgs: orgs.length,
        activeOrgs,
        pendingOrgs,
        suspendedOrgs,
        trialOrgs,
        totalLocations: locs.length,
        recentSignups,
        orgsByPlan
      });
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching stats:", error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading dashboard...</div>;
  }

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'suspended': return 'text-red-600 bg-red-50';
      case 'trial': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Dashboard Overview</h2>
        <p className="text-gray-600">Platform statistics and insights</p>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Organizations</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalOrgs}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="p-6 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700">Active Organizations</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats.activeOrgs}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="p-6 bg-yellow-50 rounded-lg border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-700">Pending Approval</p>
              <p className="text-3xl font-bold text-yellow-600 mt-1">{stats.pendingOrgs}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Locations</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalLocations}</p>
            </div>
            <div className="p-3 bg-gray-200 rounded-lg">
              <MapPin className="h-6 w-6 text-gray-600" />
            </div>
          </div>
        </div>
      </div>


      {/* Secondary Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <div className="p-6 bg-white border border-gray-200 rounded-lg">
          <h3 className="text-lg font-bold mb-4">Organizations by Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Active</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full" 
                    style={{width: `${(stats.activeOrgs / stats.totalOrgs) * 100}%`}}
                  />
                </div>
                <span className="font-bold w-8 text-right">{stats.activeOrgs}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Pending</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 rounded-full" 
                    style={{width: `${(stats.pendingOrgs / stats.totalOrgs) * 100}%`}}
                  />
                </div>
                <span className="font-bold w-8 text-right">{stats.pendingOrgs}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Trial</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded-full" 
                    style={{width: `${(stats.trialOrgs / stats.totalOrgs) * 100}%`}}
                  />
                </div>
                <span className="font-bold w-8 text-right">{stats.trialOrgs}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Suspended</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-red-500 rounded-full" 
                    style={{width: `${(stats.suspendedOrgs / stats.totalOrgs) * 100}%`}}
                  />
                </div>
                <span className="font-bold w-8 text-right">{stats.suspendedOrgs}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Signups */}
      <div className="p-6 bg-white border border-gray-200 rounded-lg">
        <h3 className="text-lg font-bold mb-4">Recent Signups</h3>
        <div className="space-y-3">
          {stats.recentSignups.map((org) => (
            <div key={org.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded">
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{org.name}</p>
                  <p className="text-sm text-gray-500 font-mono">{org.slug}</p>
                </div>
              </div>
              <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(org.status)}`}>
                  {org.status}
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(org.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
          {stats.recentSignups.length === 0 && (
            <p className="text-center text-gray-500 py-4">No recent signups</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;
